import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

type ClaimIdChipProps = {
  claimId: string;
  /** Off inside the thread itself, where the doorway would lead back to the same page. */
  linkToThread?: boolean;
};

/**
 * The universal handle. Wherever a `claim_id` appears in this product it renders through here, so
 * the route into the thread is identical from every workspace (Design System §3.1).
 *
 * Mono is functional, not stylistic: a mono span in Pressentia means *this is a key you can trace with*
 * (Art Direction §9).
 */
export function ClaimIdChip({ claimId, linkToThread = true }: ClaimIdChipProps) {
  const { streamId } = useParams<{ streamId: string }>();
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(claimId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard is unavailable over plain HTTP and in some embedded browsers. The id is
      // selectable text either way, so failing silently costs the editor nothing.
    }
  }

  return (
    <span className="claim-chip">
      {linkToThread && streamId ? (
        <Link className="claim-chip__id" to={`/stream/${encodeURIComponent(streamId)}/claim/${claimId}`}>
          {claimId}
        </Link>
      ) : (
        <span className="claim-chip__id">{claimId}</span>
      )}
      <button type="button" className="claim-chip__copy" onClick={copy} aria-label={`Copiar ${claimId}`}>
        {copied ? '✓' : '⧉'}
      </button>
    </span>
  );
}
